import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Calendar, Briefcase, MapPin, Clock, Heart, Sun, FileText, ShieldCheck, AlertTriangle, Sparkles } from 'lucide-react';
import { MarkdownView } from '../components/MarkdownEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMP_TYPE_LABEL = {
  full_time: 'Full-time', contract: 'Contractor', internship: 'Internship',
  freelancer: 'Freelancer', volunteer: 'Volunteer (unpaid)',
};

const fmtINR = (n) => n == null ? '—' : '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });

export default function PublicOffer() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declined, setDeclined] = useState(false);
  const [accepted, setAccepted] = useState(null); // { name, employee_id }
  const [ackRR, setAckRR]   = useState(false);
  const [ackTNC, setAckTNC] = useState(false);

  /* eslint-disable */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/offer/${token}`);
        setOffer(data);
        if (data.status === 'declined') setDeclined(true);
        if (data.already_accepted && data.status !== 'offer_sent') {
          setAccepted({ name: data.name, employee_id: null });
        }
      } catch (e) {
        setError(e.response?.data?.detail || 'Unable to load this offer.');
      } finally { setLoading(false); }
    })();
  }, [token]);
  /* eslint-enable */

  const accept = async () => {
    if (!ackRR || !ackTNC) {
      toast.error('Please acknowledge both the Roles & Responsibilities and the Terms & Conditions to continue.');
      return;
    }
    setAccepting(true);
    try {
      const { data } = await axios.post(`${API}/public/offer/${token}/accept`, {
        accept_roles_responsibilities: true,
        accept_terms_conditions: true,
      });
      setAccepted({ name: data.name, employee_id: data.employee_id });
      toast.success('Offer accepted! Check your inbox for login credentials.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to accept offer.'); }
    finally { setAccepting(false); }
  };

  const decline = async () => {
    if (!declineReason.trim()) { toast.error('Please share a brief reason'); return; }
    setAccepting(true);
    try {
      await axios.post(`${API}/public/offer/${token}/decline`, { reason: declineReason });
      setDeclined(true);
      setDeclineOpen(false);
      toast.info('We\u2019ve noted your decision. Thank you for letting us know.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to decline.'); }
    finally { setAccepting(false); }
  };

  if (loading) return <CenteredCard><div className="text-slate-500">Loading offer…</div></CenteredCard>;
  if (error) return <CenteredCard><div className="text-red-600 flex items-center gap-2 justify-center"><AlertTriangle className="w-5 h-5" /> {error}</div></CenteredCard>;

  if (accepted) return (
    <CenteredCard wide>
      <div className="text-center py-6" data-testid="offer-accepted-screen">
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
          <CheckCircle2 className="w-9 h-9" />
        </div>
        <h2 className="font-heading text-3xl font-bold text-slate-900">Welcome aboard, {accepted.name?.split(' ')[0] || 'there'}!</h2>
        <p className="text-slate-500 mt-2 max-w-md mx-auto">
          Your acceptance has been recorded. {accepted.employee_id && <>Your Employee ID is <b className="text-slate-900">{accepted.employee_id}</b>.</>}
        </p>
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm text-blue-900 max-w-md mx-auto text-left">
          <div className="flex items-center gap-2 font-semibold"><FileText className="w-4 h-4" /> What happens next?</div>
          <ol className="mt-2 space-y-1 list-decimal list-inside text-blue-800/90">
            <li>Check your inbox — we just emailed your <b>temporary login credentials</b>.</li>
            <li>Log in and upload your background-check documents (Aadhar, PAN, education certs).</li>
            <li>HR verifies BGC. After clearance you&apos;ll receive the formal offer letter PDF — <b>sign it and upload the signed PDF</b> back in the HRMS.</li>
            <li>HR reviews the signed offer; your work email and full access activate on approval.</li>
          </ol>
        </div>
        <Link to="/login" className="inline-block mt-6 text-[#3D4A8E] font-semibold hover:underline">Already received credentials? Sign in →</Link>
      </div>
    </CenteredCard>
  );

  if (declined) return (
    <CenteredCard wide>
      <div className="text-center py-8">
        <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mb-4">
          <XCircle className="w-9 h-9" />
        </div>
        <h2 className="font-heading text-3xl font-bold text-slate-900">Offer declined</h2>
        <p className="text-slate-500 mt-2 max-w-md mx-auto">
          Thank you for letting us know. We&apos;ve notified the hiring team. Wishing you the best!
        </p>
      </div>
    </CenteredCard>
  );

  if (offer?.expired) return (
    <CenteredCard wide>
      <div className="text-center py-8" data-testid="offer-expired-screen">
        <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mb-4">
          <AlertTriangle className="w-9 h-9" />
        </div>
        <h2 className="font-heading text-3xl font-bold text-slate-900">This offer has expired</h2>
        <p className="text-slate-500 mt-2 max-w-md mx-auto">
          Offers are valid for 7 days from the date they&apos;re sent. This offer was sent on
          <b className="text-slate-700"> {offer.offer_sent_at?.slice(0, 10) || '—'}</b> and expired on
          <b className="text-slate-700"> {offer.offer_expires_at?.slice(0, 10) || '—'}</b>.
          <br /><br />
          If you&apos;d still like to join {offer.company_name}, please reach out to HR and they can issue a fresh offer.
        </p>
      </div>
    </CenteredCard>
  );

  const t = offer.offer_terms || {};
  const payLine = (() => {
    if (offer.employment_type === 'volunteer') return 'Volunteer — unpaid position';
    if (offer.pay_amount) {
      const period = { yearly: '/ year', monthly: '/ month', hourly: '/ hour' }[offer.salary_period] || '';
      return `${fmtINR(offer.pay_amount)} ${period}`;
    }
    if (offer.annual_ctc) return `${fmtINR(offer.annual_ctc)} / year (CTC)`;
    return '—';
  })();

  const canAccept = ackRR && ackTNC && !accepting;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EEF1FA] via-white to-[#EEF1FA] py-12 px-4">
      <div className="max-w-3xl mx-auto" data-testid="public-offer-page">
        {/* Brand header — uses THE CANDIDATE'S tenant company, never a global default */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden bg-white border border-slate-200"
               style={{ backgroundColor: offer.brand_color || '#3D4A8E' }}>
            <img src={offer.company_logo_url || '/lanvaus-logo.png'} alt={offer.company_name || 'Company'} className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">{offer.company_name}</div>
            <div className="font-heading text-lg font-semibold text-slate-900">Offer of Employment</div>
          </div>
        </div>

        {/* Hero */}
        <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 inline-flex items-center gap-1.5 bg-emerald-50 px-2.5 py-1 rounded-full">
            <Sparkles className="w-3.5 h-3.5" /> You have an offer
          </div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 mt-4">
            Hi {offer.name?.split(' ')[0] || 'there'} — welcome to {offer.company_name}!
          </h1>
          <p className="text-slate-500 mt-3 text-lg">
            We&apos;re excited to offer you the role of <b className="text-slate-900">{offer.title || 'Team Member'}</b>
            {offer.grade ? ` (Grade ${offer.grade})` : ''} as a <b className="text-slate-900">{EMP_TYPE_LABEL[offer.employment_type] || 'team member'}</b>.
          </p>

          {/* Key terms grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-8">
            <Term icon={Briefcase} label="Role"        value={offer.title || '—'} testid="t-role" />
            <Term icon={FileText}  label="Department"  value={offer.department || '—'} testid="t-dept" />
            <Term icon={Calendar}  label="Joining"     value={offer.joined_at || '—'} testid="t-joined" />
            <Term icon={MapPin}    label="Location"    value={offer.branch?.city || offer.branch?.name || 'TBD'} testid="t-location" />
            <Term icon={Clock}     label="Weekly hours" value={`${t.weekly_hours ?? 40} hrs / week`} testid="t-hours" />
            <Term icon={ShieldCheck} label="Probation"   value={`${t.probation_months ?? 3} months`} testid="t-probation" />
            <Term icon={Sun}       label="Casual leave"  value={`${t.casual_leaves ?? 10} days / year`} testid="t-cl" />
            <Term icon={Heart}     label="Sick leave"    value={`${t.sick_leaves ?? 5} days / year`} testid="t-sl" />
            <Term icon={Briefcase} label="Compensation" value={payLine} testid="t-pay" wide />
          </div>

          {offer.reporting_manager_name && (
            <div className="mt-5 text-sm text-slate-500">
              You&apos;ll be reporting to <b className="text-slate-900">{offer.reporting_manager_name}</b>.
            </div>
          )}

          {/* Roles & Responsibilities */}
          <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50/40 p-5" data-testid="offer-rr-section">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#3D4A8E] mb-2">
              <FileText className="w-3.5 h-3.5" /> Roles &amp; Responsibilities
            </div>
            <div className="max-h-72 overflow-y-auto pr-1" data-testid="offer-rr-content">
              <MarkdownView source={offer.roles_responsibilities || ''} />
            </div>
          </div>

          {/* Terms & Conditions */}
          <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50/40 p-5" data-testid="offer-tnc-section">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#3D4A8E] mb-2">
              <ShieldCheck className="w-3.5 h-3.5" /> Terms &amp; Conditions
            </div>
            <div className="max-h-72 overflow-y-auto pr-1" data-testid="offer-tnc-content">
              <MarkdownView source={offer.terms_conditions || ''} />
            </div>
          </div>

          {/* Acknowledgement checkboxes */}
          <div className="mt-6 rounded-2xl border-2 border-[#3D4A8E]/30 bg-indigo-50/30 p-5 space-y-3" data-testid="offer-acknowledgement">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#3D4A8E]">Acknowledgement required</div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={ackRR} onCheckedChange={setAckRR}
                        data-testid="ack-rr-checkbox"
                        className="mt-0.5 data-[state=checked]:bg-[#3D4A8E] data-[state=checked]:border-[#3D4A8E]" />
              <span className="text-sm text-slate-700">
                I have read, understood and agree to the <b>Roles &amp; Responsibilities</b> outlined above.
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={ackTNC} onCheckedChange={setAckTNC}
                        data-testid="ack-tnc-checkbox"
                        className="mt-0.5 data-[state=checked]:bg-[#3D4A8E] data-[state=checked]:border-[#3D4A8E]" />
              <span className="text-sm text-slate-700">
                I have read, understood and agree to the <b>Terms &amp; Conditions</b> outlined above.
              </span>
            </label>
          </div>

          {/* What happens after acceptance */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm text-blue-900">
            <div className="flex items-center gap-2 font-semibold"><FileText className="w-4 h-4" /> What happens after you accept</div>
            <ol className="mt-2 space-y-1 list-decimal list-inside text-blue-800/90">
              <li>We&apos;ll email you a <b>temporary password</b> within seconds.</li>
              <li>You log in to upload your background-check documents (Aadhar, PAN, etc.).</li>
              <li>HR verifies BGC. On clearance you&apos;ll get the formal offer letter PDF.</li>
              <li><b>Sign the PDF</b> and upload it back via the HRMS dashboard. HR reviews and approves it.</li>
              <li>Your work email and full access activate on final approval.</li>
            </ol>
          </div>

          {/* Action buttons */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button onClick={accept} disabled={!canAccept} data-testid="accept-offer-btn"
                    className="flex-1 h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:text-slate-500 text-white text-base font-medium">
              <CheckCircle2 className="w-5 h-5 mr-2" /> {accepting ? 'Submitting…' : 'Accept offer'}
            </Button>
            <Button onClick={() => setDeclineOpen(true)} disabled={accepting} variant="outline" data-testid="decline-offer-btn"
                    className="flex-1 h-14 rounded-full border-slate-300 text-slate-700 hover:bg-slate-50 text-base font-medium">
              <XCircle className="w-5 h-5 mr-2" /> Decline
            </Button>
          </div>
          {!canAccept && !accepting && (
            <p className="text-xs text-slate-500 mt-3 text-center" data-testid="ack-hint">
              Tick both acknowledgement boxes above to enable the Accept button.
            </p>
          )}
        </div>

        <div className="text-center mt-8 text-xs text-slate-400">
          Sent on {offer.offer_sent_at?.slice(0, 10) || '—'} · <b className="text-amber-700">Expires on {offer.offer_expires_at?.slice(0, 10) || '—'}</b> · This is a one-time link tied to your candidacy.
        </div>
      </div>

      {/* Decline modal (inline) */}
      {declineOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => !accepting && setDeclineOpen(false)}>
          <div className="bg-white rounded-3xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-2xl font-semibold text-slate-900">Decline this offer?</h3>
            <p className="text-slate-500 mt-1 text-sm">We&apos;d appreciate a brief reason — it helps our recruiting team improve.</p>
            <Textarea data-testid="decline-reason" rows={4} value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
                      placeholder="e.g. accepted another offer, compensation, location, etc." className="rounded-xl mt-4" />
            <div className="flex gap-3 mt-5">
              <Button variant="outline" onClick={() => setDeclineOpen(false)} disabled={accepting} className="flex-1 rounded-full">Cancel</Button>
              <Button onClick={decline} disabled={accepting || !declineReason.trim()} data-testid="confirm-decline"
                      className="flex-1 rounded-full bg-red-600 hover:bg-red-700 text-white">
                {accepting ? 'Submitting…' : 'Confirm decline'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CenteredCard({ children, wide }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EEF1FA] via-white to-[#EEF1FA] p-4">
      <div className={`bg-white border border-slate-200 rounded-3xl p-10 ${wide ? 'max-w-xl' : 'max-w-md'} w-full shadow-sm`}>{children}</div>
    </div>
  );
}

function Term({ icon: Icon, label, value, testid, wide }) {
  return (
    <div data-testid={testid} className={`rounded-2xl border border-slate-200 p-4 bg-slate-50/40 ${wide ? 'col-span-2 sm:col-span-3' : ''}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="mt-1 font-semibold text-slate-900 text-sm">{value}</div>
    </div>
  );
}
